// Google Analytics 4 helpers.
//
// The tag itself is loaded in public/index.html (Consent Mode v2: ads
// denied, analytics_storage granted). Page views, including SPA route
// changes, outbound clicks, file downloads, form starts and the single 90 %
// scroll event come from the GA4 stream's enhanced measurement. Everything
// here adds the reading signal that enhanced measurement does not give us:
//
//   section_view     a <section> became at least 40 % visible, once per
//                    section per page view. Answers "what did they read".
//   scroll_depth     25 / 50 / 75 / 100 % of the page, once each per page
//                    view (GA4's own `scroll` only fires at 90 %).
//   cta_click        any link or button click, with its label, destination
//                    and the section it sits in.
//   book_demo_click  the subset of cta_click that heads to the contact form.
//                    Registered as a key event in the property.
//   page_exit        fired when the tab is hidden or the route changes:
//                    the deepest section reached, max scroll %, visible
//                    seconds. Answers "where did they stop".
//   generate_lead    contact form sent (ContactForm.jsx), with intent + role.
//
// Parameter names match the event-scoped custom dimensions / metrics
// registered on GA4 property 557913325 (section_name, section_index,
// cta_label, cta_location, cta_destination, lead_intent, lead_role,
// last_section, max_scroll_percent, time_on_page_seconds). No PII is sent:
// labels are button text, destinations are URLs, never form values.
//
// Every helper is a no-op when gtag is absent (tests, ad blockers, SSR).

import { useEffect } from "react";

export const trackEvent = (name, params = {}) => {
  if (typeof window === "undefined" || typeof window.gtag !== "function") return;
  window.gtag("event", name, params);
};

const SCROLL_MARKS = [25, 50, 75, 100];
const SECTION_VISIBLE_RATIO = 0.4;
const MAX_LABEL = 80;

const clean = (text) =>
  (text || "").replace(/\s+/g, " ").trim().slice(0, MAX_LABEL);

// A stable, human-readable name for a <section>: an explicit data-section,
// else its id, else its eyebrow, else its first heading.
const sectionName = (el) => {
  if (!el) return "";
  if (el.dataset && el.dataset.section) return el.dataset.section;
  if (el.id) return el.id;
  const eyebrow = el.querySelector(".section-eyebrow");
  if (eyebrow) return clean(eyebrow.textContent);
  const heading = el.querySelector("h1, h2, h3");
  if (heading) return clean(heading.textContent);
  return "";
};

const ctaLocation = (el) => {
  if (el.closest("header")) return "header";
  if (el.closest("footer")) return "footer";
  const section = el.closest("section");
  return section ? sectionName(section) : "page";
};

const scrollPercent = () => {
  const doc = document.documentElement;
  const scrollable = doc.scrollHeight - window.innerHeight;
  if (scrollable <= 0) return 100;
  return Math.min(100, Math.round(((window.scrollY || doc.scrollTop) / scrollable) * 100));
};

// Visible-time accounting: only count seconds while the tab is visible.
const createTimer = () => {
  let total = 0;
  let since = document.visibilityState === "visible" ? Date.now() : null;
  return {
    pause() {
      if (since !== null) {
        total += Date.now() - since;
        since = null;
      }
    },
    resume() {
      if (since === null && document.visibilityState === "visible") since = Date.now();
    },
    seconds() {
      const live = since !== null ? Date.now() - since : 0;
      return Math.round((total + live) / 1000);
    },
  };
};

/**
 * Per-page reading analytics. Call once from the layout; re-runs on every
 * route change so each page view gets its own section / scroll / exit set.
 */
export const useReadingAnalytics = (pathname) => {
  useEffect(() => {
    if (typeof window === "undefined" || typeof document === "undefined") return undefined;

    const pagePath = pathname;
    const timer = createTimer();
    const seenSections = new Set();
    const firedMarks = new Set();
    let deepestIndex = -1;
    let deepestName = "";
    let maxScroll = 0;
    let exited = false;

    // --- section views -------------------------------------------------
    const sections = Array.from(document.querySelectorAll("main section"));
    sections.forEach((el, i) => {
      if (!el.dataset.sectionIndex) el.dataset.sectionIndex = String(i);
    });

    let observer = null;
    if (typeof IntersectionObserver === "function") {
      observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            const viewportH = entry.rootBounds ? entry.rootBounds.height : window.innerHeight;
            const fillsViewport = entry.intersectionRect.height >= 0.6 * viewportH;
            if (entry.intersectionRatio < SECTION_VISIBLE_RATIO && !fillsViewport) return;
            const el = entry.target;
            const index = Number(el.dataset.sectionIndex);
            const name = sectionName(el) || `section-${index + 1}`;
            if (seenSections.has(index)) return;
            seenSections.add(index);
            if (index > deepestIndex) {
              deepestIndex = index;
              deepestName = name;
            }
            trackEvent("section_view", {
              section_name: name,
              section_index: index + 1,
              page_path: pagePath,
            });
            observer.unobserve(el);
          });
        },
        // Tall sections never reach 40 % of their own height on a short
        // viewport, hence the fillsViewport fallback above; the stepped
        // thresholds make sure the callback runs as they scroll through.
        { threshold: [0, 0.1, 0.2, 0.3, SECTION_VISIBLE_RATIO], rootMargin: "0px 0px -10% 0px" }
      );
      sections.forEach((el) => observer.observe(el));
    }

    // --- scroll depth --------------------------------------------------
    const onScroll = () => {
      const pct = scrollPercent();
      if (pct > maxScroll) maxScroll = pct;
      SCROLL_MARKS.forEach((mark) => {
        if (pct >= mark && !firedMarks.has(mark)) {
          firedMarks.add(mark);
          trackEvent("scroll_depth", { percent_scrolled: mark, page_path: pagePath });
        }
      });
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    // Deferred: the layout resets scroll to the top on route change in a
    // later effect, so reading the position synchronously here would
    // credit the new page with the previous page's scroll depth.
    const initialScroll = window.setTimeout(onScroll, 0);

    // --- CTA clicks ----------------------------------------------------
    const onClick = (e) => {
      const target = e.target instanceof Element ? e.target.closest("a, button") : null;
      if (!target) return;
      const label = clean(target.getAttribute("aria-label") || target.textContent);
      if (!label) return;
      const href = target.getAttribute("href") || "";
      const location = ctaLocation(target);
      const params = {
        cta_label: label,
        cta_location: location,
        cta_destination: href || (target.getAttribute("type") === "submit" ? "submit" : "button"),
        page_path: pagePath,
      };
      trackEvent("cta_click", params);
      if (href.startsWith("/contact")) trackEvent("book_demo_click", params);
    };
    document.addEventListener("click", onClick, true);

    // --- exit ----------------------------------------------------------
    const sendExit = () => {
      if (exited) return;
      exited = true;
      timer.pause();
      trackEvent("page_exit", {
        page_path: pagePath,
        last_section: deepestName || "hero",
        last_section_index: deepestIndex + 1,
        sections_seen: seenSections.size,
        sections_total: sections.length,
        max_scroll_percent: Math.max(maxScroll, scrollPercent()),
        time_on_page_seconds: timer.seconds(),
        transport_type: "beacon",
      });
    };
    const onVisibility = () => {
      if (document.visibilityState === "hidden") {
        sendExit();
      } else {
        // Came back: allow another exit for this same page view.
        exited = false;
        timer.resume();
      }
    };
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("pagehide", sendExit);

    return () => {
      window.clearTimeout(initialScroll);
      sendExit();
      if (observer) observer.disconnect();
      window.removeEventListener("scroll", onScroll);
      document.removeEventListener("click", onClick, true);
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("pagehide", sendExit);
    };
  }, [pathname]);
};
